import { ButtonProps, Tooltip } from "@chakra-ui/react";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import {
  InsufficientPermissionTooltipLabel,
  Restricted,
} from "@/app/components/Restricted";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { StagePicker } from "@/common/components/StagePicker";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useEnvironmentsData } from "@/environment/data/useEnvironmentsData";
import { useStagesData } from "@/stage/data/useStagesData";
import { useFlag } from "@reflag/react-sdk";
import { FeatureName } from "@reflag/shared/featureAPI";
import { UpdateEnvironmentFeatureTargeting } from "@reflag/shared/featureTargetingAPI";
import { StageDTO } from "@reflag/shared/stageAPI";
import {
  EnvironmentFeatureTargeting,
  expandEnvironmentTargeting,
  mergeTargeting,
  updateToEnvironmentFeatureTargeting,
} from "@reflag/shared/utils/targetingRule";

function stageHasProductionTargeting(stage: StageDTO) {
  return stage.stageEnvironments.some(
    (e) => e.environment.isProduction && e.targetingMode !== "none",
  );
}

type Props = Omit<ButtonProps, "onChange"> & {
  feature: FeatureName;
  stageId?: string | null;
  targetingSnapshot: UpdateEnvironmentFeatureTargeting[];
  isDisabled?: boolean;
  disabledReason?: string;
  onStageChange?: (stageId: string) => void;
};

export const FormFeatureStagePicker = ({
  feature,
  stageId,
  targetingSnapshot,
  isDisabled = false,
  disabledReason,
  onStageChange: onStageChangeProp,
  ...rest
}: Props) => {
  const { setValue } = useFormContext();
  const { userHasRole } = useEnvironment();

  const { data: environments = [] } = useEnvironmentsData();
  const { data: stages = [] } = useStagesData();

  const { isEnabled: enforceUserPermissions } = useFlag("user-permissions");
  const isContributor = userHasRole("contributor") || !enforceUserPermissions;
  const isPublisher = userHasRole("publisher") || !enforceUserPermissions;

  const currentStage = stages.find((s) => s.id === stageId) ?? stages[0];

  const expandVersions = useCallback(
    (versions: EnvironmentFeatureTargeting[]) =>
      expandEnvironmentTargeting(environments, versions),
    [environments],
  );

  const onStageChange = useCallback(
    (newStageId: string) => {
      if (newStageId === stageId) return;

      const newStage = stages.find((s) => s.id === newStageId);
      if (!newStage) return;

      const newVersions = mergeTargeting(
        expandVersions(updateToEnvironmentFeatureTargeting(targetingSnapshot)),
        expandVersions(newStage.stageEnvironments),
      );

      setValue("versions", newVersions, {
        shouldDirty: true,
      });
      onStageChangeProp?.(newStageId);
    },
    [
      expandVersions,
      stages,
      targetingSnapshot,
      stageId,
      setValue,
      onStageChangeProp,
    ],
  );

  const disabledFilterFn = useCallback(
    (stage: StageDTO) => {
      // If the current stage has production targeting or the new stage has production targeting,
      // and the user is not a publisher or higher, disable the stage
      const isDisabled =
        (stageHasProductionTargeting(currentStage) ||
          stageHasProductionTargeting(stage)) &&
        !isPublisher;

      return {
        isDisabled: isContributor && isDisabled,
        tooltip: isDisabled ? (
          <InsufficientPermissionTooltipLabel role="publisher" />
        ) : undefined,
      };
    },
    [currentStage, isContributor, isPublisher],
  );

  return (
    <Restricted alertStyle="none" feature={feature} role="contributor">
      <ManagedFormControl
        name="stageId"
        render={({ field }) => (
          <Tooltip
            label={isDisabled ? disabledReason : undefined}
            openDelay={300}
            hasArrow
          >
            <StagePicker
              {...field}
              _group={{ w: "full" }}
              disabledFilterFn={disabledFilterFn}
              isDisabled={isDisabled}
              menuDescription="Select the flag stage"
              onChange={(value) => {
                field.onChange(value);
                onStageChange(value);
              }}
              {...rest}
            />
          </Tooltip>
        )}
      />
    </Restricted>
  );
};
